import { createColumnHelper } from "@tanstack/react-table";
import {
  Account,
  AuthConfig,
  BackgroundJobTypeName,
  Control,
  ControlCode,
  formatDate,
  getControlName,
  JobStatusPerJobType,
  ListJobStatusResponse,
  parseDate,
  queries,
  TimeFormats,
  useAuthConfig,
} from "@theprelude/core";
import {
  CheckmarkIcon,
  CloseIcon,
  ControlIcon,
  IconButton,
  InfoIcon,
  LoaderIcon,
  ShieldUser,
  Truncate,
} from "@theprelude/ds";
import { z } from "zod";
import { createQuery } from "@/lib/create-query";
import { DefaultOverlay } from "../default-overlay";
import { DefaultTooltip } from "../default-tooltip";
import { useTable } from "../table/hooks";
import { Unavailable } from "../table/unavaliable";
import { VirtualTable } from "../table/virtual-table";

export const eventLogSchema = z.tuple([z.literal("event-log")]);

export type EventLogRow = JobStatusPerJobType & {
  type: BackgroundJobTypeName;
  instanceName: string | undefined;
};

const eventLogQuery = (authConfig: AuthConfig) =>
  createQuery({
    queries: [
      queries.iam.getAccount(authConfig),
      queries.jobs.getJobStatuses(authConfig),
    ],
    queryFn: formatEventLog,
  });

const formatEventLog = (
  account: Account,
  jobStatuses: ListJobStatusResponse,
): {
  eventLogRows: EventLogRow[];
} => {
  const controlMap = account.controls.reduce((acc, control) => {
    acc.set(control.id, control);
    return acc;
  }, new Map<ControlCode, Control>());

  const jobTypes = Object.keys(jobStatuses) as BackgroundJobTypeName[];
  const eventLogRows = jobTypes.flatMap((type) => {
    return (
      jobStatuses[type]?.map(
        (job): EventLogRow => ({
          ...job,
          type,
          instanceName:
            (job.control && controlMap.get(job.control)?.name) || undefined,
        }),
      ) ?? []
    );
  });

  return { eventLogRows };
};

export function EventLogOverlay() {
  return (
    <DefaultOverlay title={"Event Log"}>
      <EventLogView />
    </DefaultOverlay>
  );
}

const EventLogView = () => {
  const authConfig = useAuthConfig();
  const {
    data: { eventLogRows },
  } = eventLogQuery(authConfig).useSuspenseQuery();

  const table = useTable({
    name: "event-log",
    data: eventLogRows,
    columns,
    initialState: {
      sorting: [{ id: "start_time", desc: true }],
    },
  });

  return (
    <div className="flex flex-col gap-6 p-4">
      <VirtualTable table={table} className="max-h-[calc(100vh-200px)]" />
    </div>
  );
};

const columnHelper = createColumnHelper<EventLogRow>();

const columns = [
  columnHelper.accessor("control", {
    id: "control",
    cell: (info) => {
      const value = info.getValue();

      if (value === null) {
        return (
          <div className="flex min-w-0 items-center gap-2">
            <ShieldUser className="h-4 w-4 flex-shrink-0 text-secondary-foreground" />
            <Truncate text="All Controls">All Controls</Truncate>
          </div>
        );
      }

      const name = info.row.original.instanceName;

      const controlName = [getControlName(value), name]
        .filter(Boolean)
        .join(" ");

      return (
        <div className="flex min-w-0 items-center gap-2">
          <ControlIcon
            className="h-4 w-4 flex-shrink-0 text-secondary-foreground"
            control={value}
          />
          <Truncate text={controlName}>
            <span>{getControlName(value)}</span>
            {name && (
              <span className="text-sm text-secondary-foreground">
                &nbsp;&nbsp;{name}
              </span>
            )}
          </Truncate>
        </div>
      );
    },
    meta: {
      label: "Control",
      widths: ["120px", "1fr"],
    },
  }),
  columnHelper.accessor("type", {
    id: "type",
    cell: (info) => {
      const value = getPartnerJobTypeName(info.getValue());
      return <Truncate text={value}>{value}</Truncate>;
    },
    meta: {
      label: "Job Type",
      widths: ["120px", "1fr"],
    },
  }),
  columnHelper.accessor("start_time", {
    id: "start_time",
    cell: (info) => {
      const value = info.getValue();
      const formattedDate = formatDate(
        parseDate(value),
        TimeFormats.DATE_FIRST,
      );
      return <Truncate text={formattedDate}>{formattedDate}</Truncate>;
    },
    meta: {
      label: "Start Time",
      widths: ["120px", "1fr"],
    },
  }),
  columnHelper.accessor("end_time", {
    id: "end_time",
    cell: (info) => {
      const value = info.getValue();
      if (value === null) {
        return <Unavailable />;
      }

      const formattedDate = formatDate(
        parseDate(value),
        TimeFormats.DATE_FIRST,
      );
      return <Truncate text={formattedDate}>{formattedDate}</Truncate>;
    },
    meta: {
      label: "End Time",
      widths: ["120px", "1fr"],
    },
  }),
  columnHelper.accessor("successful", {
    id: "successful",
    cell: (info) => {
      const value = info.getValue();

      if (value === null) {
        return (
          <div className="text-secondary flex items-center gap-1">
            <LoaderIcon />
            <span>Pending</span>
          </div>
        );
      }

      if (value) {
        return (
          <div className="flex items-center gap-1 text-status-green">
            <CheckmarkIcon />
            <span>Success</span>
          </div>
        );
      }

      const errorMessage = info.row.original.error;

      return (
        <div className="flex items-center gap-1 text-status-red">
          <CloseIcon />
          <span>Failed</span>
          {errorMessage && (
            <DefaultTooltip
              asChild
              content={<div className="max-w-[500px]">{errorMessage}</div>}
            >
              <IconButton
                className="inline-flex text-status-red"
                icon={<InfoIcon />}
              />
            </DefaultTooltip>
          )}
        </div>
      );
    },
    meta: {
      label: "Status",
      widths: ["80px", "1fr"],
    },
  }),
];

export const getPartnerJobTypeName = (type: BackgroundJobTypeName) => {
  switch (type) {
    case "INVALID": {
      return "Invalid";
    }
    case "UPDATE_SCM": {
      return "Policy Evaluation";
    }
    case "DEPLOY_PROBE": {
      return "Deploy Probe";
    }
    case "OBSERVED_DETECTED": {
      return "Observed Detected";
    }
    case "PRELUDE_ENDPOINT_SYNC": {
      return "Prelude Endpoint Sync";
    }
    case "EXPORT_SCM": {
      return "Export SCM";
    }
    default: {
      return "Unknown";
    }
  }
};
